import { Component, HostListener, Inject, OnInit, Optional } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserNotificationItem } from 'src/app/model/userNotificationItem';
import { UserNotificationService } from 'src/app/service/user.notification.service';
import { UserNotificationPreviewComponent } from '../user-notification-preview/user-notification-preview.component';

// Central de Notificações — página `/notifications` (autor/revisor). Redesenho
// JEMS3 (Tiers A+B+C+D): header fixo com sticky is-scrolled, lista de cards em
// vez de <table>, filtro por escopo (Todas / Não lidas), busca sempre cobrindo
// subject + content, timestamp humano relativo, ações "Recarregar" e "Marcar
// todas como lidas". Dark mode e a11y explícitos no SCSS/template.
@Component({
  selector: 'app-notifications-center',
  templateUrl: './notifications-center.component.html',
  styleUrls: ['./notifications-center.component.scss']
})
export class NotificationsCenterComponent implements OnInit {
  public notificationList: UserNotificationItem[] = [];
  public notificationListFiltered: UserNotificationItem[] = [];
  public isLoadingInfo = false;
  public search: string;
  // Escopo do filtro — 'all' (padrão) inclui lidas + não-lidas; 'unread'
  // filtra só as pendentes. Segmented button no header (padrão JEMS3 —
  // ver home/events, my-events, submissions).
  public scope: 'all' | 'unread' = 'all';
  // Sticky header — flag setada pelo scroll da janela pra ativar o
  // `.is-scrolled` (border-bottom + box-shadow, mesma técnica das demais
  // páginas do autor/revisor).
  public isScrolled = false;

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    this.isScrolled = window.scrollY > 4;
  }

  constructor(
    private notificationService: UserNotificationService,
    private dialog: MatDialog,
    // Injetados quando o componente é aberto DENTRO de um mat-dialog
    // (ex.: pelo sino de notificações). `@Optional` porque a rota `/notifications`
    // instancia o componente diretamente, sem dialog.
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() private dialogRef: MatDialogRef<NotificationsCenterComponent>,
  ) {}

  /** True quando o componente vive dentro de um mat-dialog (sino).
   *  O SCSS usa isso pra desligar o `position: fixed` do header (ele
   *  vira in-flow dentro do dialog) e ajustar paddings pra caber num
   *  container menor. Fechar o dialog fica por conta do usuário via
   *  backdrop click / Escape (comportamento default do mat-dialog). */
  public get inDialog(): boolean {
    return !!this.dialogRef;
  }

  public closeDialog(): void {
    this.dialogRef?.close();
  }

  ngOnInit(): void {
    // No DIALOG (aberto pelo sino), o foco do usuário é "o que ainda
    // não vi" — default = escopo `unread`. Na rota `/notifications`
    // segue `all` (histórico completo é o valor da página cheia).
    if (this.inDialog) { this.scope = 'unread'; }

    // ABERTURA INSTANTÂNEA: se o serviço já tem cache (populado pelo
    // sino via `warmAllCache()`), renderiza IMEDIATAMENTE — sem
    // spinner. Continua fazendo refetch em background pra sincronizar
    // se novas notif chegaram desde o último warm-up.
    const cached = this.notificationService.allData || [];
    if (cached.length > 0) {
      this.notificationList = cached;
      this.applyFilter();
      this.isLoadingInfo = false;
    } else {
      this.isLoadingInfo = true;
    }
    this.fetchUserNotifications();
    // Refetch quando o painel do sino "limpa tudo" (evento existente).
    this.notificationService.messagesCleanedTrigger$.subscribe(() => {
      this.fetchUserNotifications();
    });
    // Refetch quando QUALQUER lugar (bell panel abrindo um email,
    // outro componente marcando lido/não-lido) muda status.
    this.notificationService.messageStatusChangeTrigger$.subscribe(() => {
      this.fetchUserNotifications();
    });
  }

  public fetchUserNotifications(): void {
    // getUserNotifications busca direto do backend (sem cache do service).
    this.notificationService.getUserNotifications().subscribe(notifs => {
      this.notificationList = notifs || [];
      this.applyFilter();
      this.isLoadingInfo = false;
    });
  }

  public openEmailView(item: UserNotificationItem): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = item;
    dialogConfig.width = '55rem';
    dialogConfig.maxHeight = '30rem';
    this.dialog.open(UserNotificationPreviewComponent, dialogConfig);
    // Abrir sempre marca como lida (padrão de inbox — ver = lido). Se o
    // usuário quiser explicitamente desmarcar, usa a ação dedicada.
    if (!item.removed) {
      this.markAsRead(item);
    }
  }

  public applyFilter(): void {
    const list = this.notificationList || [];
    const scoped = this.scope === 'unread'
      ? list.filter(n => !n.removed)
      : list;
    const q = (this.search || '').trim().toLowerCase();
    if (!q) {
      this.notificationListFiltered = scoped;
      return;
    }
    // Sempre casa subject + content (o checkbox "filtrar pelo conteúdo"
    // foi removido — o padrão inbox é buscar em ambos sem opção adicional).
    this.notificationListFiltered = scoped.filter(n =>
      (n.subject || '').toLowerCase().includes(q) ||
      (n.content || '').toLowerCase().includes(q)
    );
  }

  public setScope(scope: 'all' | 'unread'): void {
    this.scope = scope;
    this.applyFilter();
  }

  public markAsRead(notif: UserNotificationItem): void {
    this.notificationService.changeMessageReadStatus(notif.id, true).subscribe(() => {
      notif.removed = true;
      this.notificationService.emitMessageStatusChangeEvent();
      this.applyFilter();
    });
  }

  public markAsNotRead(notif: UserNotificationItem): void {
    this.notificationService.changeMessageReadStatus(notif.id, false).subscribe(() => {
      notif.removed = false;
      this.notificationService.emitMessageStatusChangeEvent();
      this.applyFilter();
    });
  }

  /** Ação global "Marcar todas como lidas". Usa o endpoint BULK do
   *  backend (`POST /core/email/byUser/all/` sem body) que faz um único
   *  UPDATE SQL em vez de disparar N HTTP POSTs em paralelo — era o
   *  gargalo pra usuários com muitas notificações. */
  public markAllAsRead(): void {
    const unread = (this.notificationList || []).filter(n => !n.removed);
    if (!unread.length) { return; }
    this.notificationService.markAllMessagesAsRead().subscribe(() => {
      unread.forEach(n => n.removed = true);
      this.notificationService.emitMessageStatusChangeEvent();
      this.applyFilter();
    });
  }

  /** Ação global "Marcar todas como NÃO lidas". Mesmo endpoint bulk,
   *  status_change=false. */
  public markAllAsUnread(): void {
    const read = (this.notificationList || []).filter(n => n.removed);
    if (!read.length) { return; }
    this.notificationService.markAllMessagesAsUnread().subscribe(() => {
      read.forEach(n => n.removed = false);
      this.notificationService.emitMessageStatusChangeEvent();
      this.applyFilter();
    });
  }

  /** Total de mensagens já lidas (usado pelo botão toggle). */
  public get readCount(): number {
    return (this.notificationList || []).filter(n => n.removed).length;
  }

  public get unreadCount(): number {
    return (this.notificationList || []).filter(n => !n.removed).length;
  }

  /** trackBy do `*ngFor` — estabiliza a identidade dos cards. */
  public trackNotifById(_index: number, item: UserNotificationItem): number {
    return item?.id;
  }

  /** Data absoluta pra tooltip do timestamp — o texto na tela é relativo
   *  (via `| relativeTime`), hover mostra o instante completo. */
  public formatAbsoluteTime(value: Date | string | null | undefined): string {
    if (!value) { return ''; }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleString();
  }
}
